import React from "react";
import { LuPackage, LuTruck, LuBadgeCheck, LuCheck } from "react-icons/lu";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";

const stats = [
  { value: "10+", label: "Years in Business" },
  { value: "DFW+", label: "Service Area" },
  { value: "1–2", label: "Day Delivery" },
  { value: "100s", label: "Products In Stock" },
];

const values = [
  { title: "One-Stop Shop",       body: "From disposable vapes and kratom to delta products, glass, and novelty items — we carry everything your store needs so you're never bouncing between suppliers.", Icon: LuPackage },
  { title: "Reliable Delivery",   body: "We serve the entire DFW metroplex and well beyond — including small towns across Texas. Once your order is placed, expect delivery in 1–2 business days.",          Icon: LuTruck },
  { title: "Proven Track Record", body: "Over a decade serving retailers across Texas. We've built our reputation on consistent stock, fair wholesale pricing, and showing up when our customers need us.", Icon: LuBadgeCheck },
];

export default function AboutUs() {
  return (
    <div className="min-h-screen bg-brand-surface font-sans">
      <Helmet>
        <title>About Us | Khoja Distributions — DFW Wholesale Distributor</title>
        <meta name="description" content="Khoja Distributions has been a trusted B2B wholesale supplier for over 10 years, serving convenience stores and smoke shops across the DFW metroplex and beyond with fast 1–2 day delivery." />
        <link rel="canonical" href="https://www.khojadistributions.com/about" />
      </Helmet>

      {/* Hero */}
      <div className="bg-brand-navy py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block bg-brand-amber/20 border border-brand-amber/30 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
            Our Story
          </span>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight tracking-tight mb-6">
            A Decade of Keeping DFW Retailers Stocked
          </h1>
          <p className="text-slate-400 text-lg leading-relaxed max-w-2xl mx-auto">
            Khoja Distributions has been serving convenience stores, smoke shops, and independent retailers across Texas for over 10 years — delivering the products your customers want, when you need them.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {stats.map((s, i) => (
            <ScrollReveal key={s.label} delay={i * 0.1}>
              <p className="text-4xl font-black text-brand-navy mb-1">{s.value}</p>
              <p className="text-sm text-slate-500 font-medium">{s.label}</p>
            </ScrollReveal>
          ))}
        </div>
      </div>

      {/* Story */}
      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <ScrollReveal direction="right">
            <span className="text-xs font-bold text-brand-amber uppercase tracking-widest block mb-3">Who We Are</span>
            <h2 className="text-3xl font-black text-brand-navy leading-tight mb-5">Your Wholesale Partner Across Texas</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Founded over 10 years ago, Khoja Distributions has grown from a local DFW supplier into a trusted wholesale partner for retailers across the entire metroplex and well into the surrounding towns and regions of Texas.
              </p>
              <p>
                We understand that small and mid-size retailers can't afford unreliable supply chains. That's why we've built our business around consistency — keeping our warehouse stocked, our delivery routes efficient, and our pricing competitive so your shelves are never empty.
              </p>
              <p>
                Whether you run a single convenience store in a small town or manage multiple locations across the metroplex, we deliver the same level of service: fast, dependable, and personal.
              </p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.15}>
            <div className="bg-slate-100 rounded-2xl p-8 space-y-4">
              {["DFW metroplex and surrounding Texas regions", "Convenience stores, smoke shops & independent retailers", "1–2 day delivery after order placement", "No bouncing between suppliers — we carry it all", "Over 10 years of proven wholesale experience"].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-brand-amber/20 border border-brand-amber/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <LuCheck size={12} className="text-brand-amber" />
                  </div>
                  <p className="text-sm text-slate-700">{item}</p>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Values */}
      <div className="bg-slate-200 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="text-xs font-bold text-brand-amber uppercase tracking-widest block mb-2">Why Retailers Choose Us</span>
              <h2 className="text-3xl font-black text-slate-900">Built Around Your Business</h2>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <ScrollReveal key={v.title} delay={i * 0.12}>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 h-full">
                  <div className="w-10 h-10 rounded-xl bg-brand-navy flex items-center justify-center mb-4">
                    <v.Icon size={20} className="text-brand-amber" />
                  </div>
                  <h3 className="text-base font-black text-brand-navy mb-2">{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <ScrollReveal>
        <div className="py-20 px-6 text-center">
          <h2 className="text-2xl font-black text-brand-navy mb-3">Ready to open a wholesale account?</h2>
          <p className="text-slate-500 text-sm mb-7">Apply online and get access to wholesale pricing across all our product categories.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register" className="bg-brand-navy hover:bg-brand-amber transition-colors text-white font-bold px-7 py-3.5 rounded-xl text-sm">
              Apply for Wholesale Access
            </Link>
            <Link to="/contact" className="border border-slate-200 text-brand-navy hover:bg-slate-50 font-bold px-7 py-3.5 rounded-xl text-sm transition-colors">
              Contact Us
            </Link>
          </div>
        </div>
      </ScrollReveal>
    </div>
  );
}
